import React, { ForwardedRef, forwardRef, memo } from 'react'
import { IconButton, Button, Tooltip, Badge, BadgeProps } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useResponsiveBreakpoint } from 'hooks/useResponsiveBreakpoint'

interface IResponsiveButtonProps {
  invert?: boolean
  disabled?: boolean
  fullWidth?: boolean
  mobileLabel?: React.ReactNode
  label?: React.ReactNode
  icon?: React.ReactNode
  tooltip?: React.ReactNode
  badgeProps?: BadgeProps
  badgeContent?: React.ReactNode
  breakpoint?: number
  children?: React.ReactNode
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  style?: React.CSSProperties
  className?: string
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
  component?: React.ElementType
  href?: string
}

const ResponsiveButton = (props: IResponsiveButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
  const theme = useTheme()
  const {
    children,
    invert,
    mobileLabel,
    label,
    icon,
    tooltip,
    badgeProps = {},
    badgeContent,
    color,
    breakpoint = theme.breakpoints.values.sm,
    ...otherProps
  } = props

  const iconButtonColor = color === 'secondary' ? 'primary' : color

  let isMobile = useResponsiveBreakpoint(breakpoint)
  let element = null

  if (invert) isMobile = !isMobile

  if (isMobile || !label) {
    if (mobileLabel) {
      element = (
        <Button ref={ref} startIcon={icon} color={color} {...otherProps as any}>
          {mobileLabel}
          {children}
        </Button>
      )
    } else {
      element = (
        <IconButton ref={ref} color={iconButtonColor} {...otherProps as any}>
          {icon}
          {children}
        </IconButton>
      )
    }
  } else if (tooltip) {
    element = (
      <Tooltip title={tooltip}>
        <Button ref={ref} startIcon={icon} color={color} {...otherProps as any}>
          {label}
          {children}
        </Button>
      </Tooltip>
    )
  } else {
    element = (
      <Button ref={ref} startIcon={icon} color={color} {...otherProps as any}>
        {label}
        {children}
      </Button>
    )
  }

  if (badgeContent) {
    return <Badge badgeContent={badgeContent} {...badgeProps}>{element}</Badge>
  }

  return element
}

export default memo(forwardRef(ResponsiveButton))
